import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Clock, Plus, Trash2, GripHorizontal, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TimelineBlock as TimelineBlockType } from "@/canvas/types/blocks";
import type { TimelinePhase } from "@/canvas/types/content";
import { cn } from "@/lib/utils";
import { useCanvasStore } from "../../store";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { generateItemId } from "../../utils/id";
import { DocumentCitationChip } from "./CitationChip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface TimelineBlockProps {
  block: TimelineBlockType;
  readOnly?: boolean;
}

const statusStyles: Record<TimelinePhase["status"], string> = {
  upcoming: "bg-card border-muted-foreground/30",
  active: "bg-card border-primary",
  completed: "bg-card border-foreground",
};

const spineStyles: Record<TimelinePhase["status"], string> = {
  upcoming: "bg-muted-foreground/20",
  active: "bg-primary",
  completed: "bg-foreground",
};

interface TimelinePhaseItemProps {
  phase: TimelinePhase;
  readOnly: boolean;
  onUpdatePhase: (updated: TimelinePhase) => void;
  onRemovePhase: (id: string) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  isLast?: boolean;
}

function TimelinePhaseItem({
  phase,
  readOnly,
  onUpdatePhase,
  onRemovePhase,
  dragHandleProps,
  isLast,
}: TimelinePhaseItemProps) {
  const { t } = useTranslation();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDate, setIsEditingDate] = useState(false);
  // Local state for editing - allows free typing without blocking keystrokes
  const [localName, setLocalName] = useState(phase.name);
  const [localDate, setLocalDate] = useState(phase.startDate || "");
  // Store the width of the title text to preserve it during editing
  const [titleWidth, setTitleWidth] = useState<number | undefined>(undefined);
  const titleRef = useRef<HTMLParagraphElement>(null);

  // Sync local state when props change externally (undo/redo, AI updates, collaborative edits)
  useEffect(() => {
    if (!isEditingName) setLocalName(phase.name);
  }, [phase.name, isEditingName]);

  useEffect(() => {
    if (!isEditingDate) setLocalDate(phase.startDate || "");
  }, [phase.startDate, isEditingDate]);

  const handleStartEditingName = useCallback(() => {
    // Measure the title width before switching to input
    if (titleRef.current) {
      setTitleWidth(titleRef.current.offsetWidth);
    }
    setLocalName(phase.name);
    setIsEditingName(true);
  }, [phase.name]);

  const handleStartEditingDate = useCallback(() => {
    setLocalDate(phase.startDate || "");
    setIsEditingDate(true);
  }, [phase.startDate]);

  const handleNameBlur = useCallback(() => {
    setIsEditingName(false);
    const trimmed = localName.trim();
    if (!trimmed) {
      // Empty name not allowed - revert to original
      setLocalName(phase.name);
    } else if (trimmed !== phase.name) {
      onUpdatePhase({ ...phase, name: trimmed });
    }
  }, [localName, phase, onUpdatePhase]);

  const handleDateBlur = useCallback(() => {
    setIsEditingDate(false);
    const trimmed = localDate.trim();
    // Accept any non-empty input - users can format dates as they prefer
    if (!trimmed) {
      if (phase.startDate) {
        onUpdatePhase({ ...phase, startDate: undefined });
      }
    } else if (trimmed !== phase.startDate) {
      onUpdatePhase({ ...phase, startDate: trimmed });
    }
  }, [localDate, phase, onUpdatePhase]);

  return (
    <div className="relative flex flex-col items-start group/phase min-w-[200px]">
      {/* Optional start date */}
      <div className="h-6 mb-2 flex items-end w-full">
        {isEditingDate && !readOnly ? (
          <Input
            value={localDate}
            onChange={(e) => setLocalDate(e.target.value)}
            onBlur={handleDateBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleDateBlur();
              } else if (e.key === "Escape") {
                setLocalDate(phase.startDate || ""); // Revert
                setIsEditingDate(false);
              }
            }}
            placeholder={t("canvas.blocks.timeline.addDate", "Add date")}
            className="text-ui-eyebrow text-muted-foreground bg-transparent border-0 border-b border-border rounded-none h-auto py-0.5 focus-visible:ring-0 focus-visible:border-foreground w-full"
            autoFocus
          />
        ) : phase.startDate || !readOnly ? (
          <p
            className={cn(
              "text-ui-eyebrow text-muted-foreground",
              !readOnly && "cursor-text hover:bg-muted rounded px-1.5 py-0.5 -ml-1.5",
              !phase.startDate && "italic",
            )}
            onClick={() => !readOnly && handleStartEditingDate()}
          >
            {phase.startDate || t("canvas.blocks.timeline.addDate", "Add date")}
          </p>
        ) : null}
      </div>

      {/* Dot and Spine */}
      <div className="relative w-full flex items-center h-4 mb-2">
        {/* Spine segment */}
        {!isLast && (
          <div
            className={cn(
              "absolute left-[7px] top-1/2 -translate-y-1/2 h-[2px] w-[calc(100%+16px)] z-0",
              spineStyles[phase.status],
            )}
          />
        )}
        {/* Phase dot */}
        <div
          className={cn(
            "relative z-10 h-3.5 w-3.5 rounded-full border-2",
            statusStyles[phase.status],
          )}
        />
      </div>

      {/* Phase name and actions */}
      <div className="w-full mb-1 flex items-center gap-1">
        {isEditingName && !readOnly ? (
          <Input
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleNameBlur();
              } else if (e.key === "Escape") {
                setLocalName(phase.name); // Revert
                setIsEditingName(false);
              }
            }}
            className="text-ui-body px-1.5 py-0.5 -ml-1.5 bg-transparent border-0 border-b border-border rounded-none h-auto focus-visible:ring-0 focus-visible:border-foreground"
            style={{ width: titleWidth ? `${titleWidth}px` : "auto", minWidth: "60px" }}
            autoFocus
          />
        ) : (
          <p
            ref={titleRef}
            className={cn(
              "text-ui-body px-1.5 py-0.5 -ml-1.5 whitespace-nowrap",
              !readOnly && "cursor-text hover:bg-muted rounded",
            )}
            onClick={() => !readOnly && handleStartEditingName()}
          >
            {phase.name}
          </p>
        )}

        {/* Actions - inline with title */}
        {!readOnly && (
          <div className="flex items-center opacity-0 group-hover/phase:opacity-100 transition-opacity">
            {dragHandleProps && (
              <div
                {...dragHandleProps}
                className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground p-1"
              >
                <GripHorizontal className="h-3.5 w-3.5" />
              </div>
            )}
            <button
              type="button"
              className="text-muted-foreground/50 hover:text-destructive p-1 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onRemovePhase(phase.id);
              }}
              title={t("canvas.blocks.timeline.removePhase", "Remove phase")}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Document references — displayed in a Popover */}
      {(phase.documentRefs?.length ?? 0) > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button className="text-ui-body-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors rounded px-1.5 py-0.5 -ml-1.5 hover:bg-muted/50">
              <FileText className="h-3 w-3" />
              {phase.documentRefs!.length}{" "}
              {phase.documentRefs!.length === 1
                ? t("canvas.blocks.timeline.source")
                : t("canvas.blocks.timeline.sources")}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2 flex flex-col gap-1" align="start">
            {(phase.documentRefs ?? []).map((ref, i) => (
              <DocumentCitationChip
                key={typeof ref === "string" ? `${ref}-${i}` : ref.id}
                fileName={typeof ref === "string" ? ref : ref.fileName}
                sourceFileId={typeof ref === "string" ? undefined : ref.sourceFileId}
                pageNumber={typeof ref === "string" ? undefined : ref.pageNumber}
              />
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

interface SortableTimelinePhaseItemProps extends Omit<TimelinePhaseItemProps, "dragHandleProps"> {
  id: string;
}

function SortableTimelinePhaseItem({ id, ...props }: SortableTimelinePhaseItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex flex-col items-start min-w-[200px]">
      <TimelinePhaseItem {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

export function TimelineBlock({ block, readOnly = false }: TimelineBlockProps) {
  const { t } = useTranslation();
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [localHeader, setLocalHeader] = useState(block.title || "");
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  const updateBlock = useCanvasStore((state) => state.updateBlock);

  const timelinePhases = useMemo<TimelinePhase[]>(() => {
    return block.content.phases ?? [];
  }, [block.content.phases]);

  // Sync header local state when prop changes externally
  useEffect(() => {
    if (!isEditingHeader) setLocalHeader(block.title || "");
  }, [block.title, isEditingHeader]);

  // Get the header text (custom title or default translation)
  const headerText = block.title || t("canvas.blocks.timeline.header", "Timeline");

  const handleStartEditingHeader = useCallback(() => {
    setLocalHeader(block.title || "");
    setIsEditingHeader(true);
  }, [block.title]);

  const handleHeaderBlur = useCallback(() => {
    setIsEditingHeader(false);
    const newTitle = localHeader.trim() || undefined;
    if (newTitle !== block.title) {
      updateBlock(block.id, { title: newTitle });
    }
  }, [localHeader, block.id, block.title, updateBlock]);

  const handleUpdatePhase = useCallback(
    (updated: TimelinePhase) => {
      const newPhases = timelinePhases.map((p) => (p.id === updated.id ? updated : p));
      updateBlockContent(block.id, {
        phases: newPhases,
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
    [block.id, timelinePhases, updateBlockContent],
  );

  const handleAddPhase = useCallback(() => {
    const newPhase: TimelinePhase = {
      id: generateItemId(),
      name: t("canvas.blocks.timeline.newPhase", "New phase"),
      status: "upcoming",
    };
    updateBlockContent(block.id, {
      phases: [...timelinePhases, newPhase],
    } as unknown as Parameters<typeof updateBlockContent>[1]);
  }, [block.id, timelinePhases, updateBlockContent, t]);

  const handleRemovePhase = useCallback(
    (phaseId: string) => {
      const newPhases = timelinePhases.filter((p) => p.id !== phaseId);
      updateBlockContent(block.id, {
        phases: newPhases,
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
    [block.id, timelinePhases, updateBlockContent],
  );

  // Drag-and-drop sensors and handler
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = timelinePhases.findIndex((p) => p.id === active.id);
      const newIndex = timelinePhases.findIndex((p) => p.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const newPhases = [...timelinePhases];
      const [removed] = newPhases.splice(oldIndex, 1);
      newPhases.splice(newIndex, 0, removed);

      updateBlockContent(block.id, {
        phases: newPhases,
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
    [block.id, timelinePhases, updateBlockContent],
  );

  if (timelinePhases.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-muted-foreground/30 p-6 text-center">
        <Clock className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
        <p className="text-sm text-muted-foreground mb-3">{t("canvas.blocks.timeline.empty")}</p>
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground hover:bg-muted border border-dashed border-transparent hover:border-border"
            onClick={handleAddPhase}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t("canvas.blocks.timeline.addPhase", "Add phase")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-baseline gap-3 mb-3">
        {isEditingHeader && !readOnly ? (
          <input
            value={localHeader}
            onChange={(e) => setLocalHeader(e.target.value)}
            onBlur={handleHeaderBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleHeaderBlur();
              } else if (e.key === "Escape") {
                setLocalHeader(block.title || ""); // Revert
                setIsEditingHeader(false);
              }
            }}
            placeholder={t("canvas.blocks.timeline.header", "Timeline")}
            className="text-ui-title-3 text-foreground bg-transparent border-0 rounded px-1.5 py-0.5 -ml-1.5 h-auto focus-visible:ring-0 w-auto max-w-full inline-block outline-none align-baseline"
            style={{ fieldSizing: "content" } as React.CSSProperties}
            autoFocus
          />
        ) : (
          <h3
            className={cn(
              "text-ui-title-3",
              !readOnly && "cursor-text hover:bg-muted rounded px-1.5 py-0.5 -ml-1.5",
            )}
            onClick={() => !readOnly && handleStartEditingHeader()}
          >
            {headerText}
          </h3>
        )}
      </div>

      {/* Timeline */}
      <div className="overflow-x-auto pb-4 pt-2 scrollbar scrollbar-track-bg">
        {readOnly ? (
          // Read-only mode: render phases directly without drag-and-drop
          <div className="relative flex items-start gap-4 w-fit">
            {timelinePhases.map((phase, index) => (
              <TimelinePhaseItem
                key={phase.id}
                phase={phase}
                readOnly={readOnly}
                onUpdatePhase={handleUpdatePhase}
                onRemovePhase={handleRemovePhase}
                isLast={index === timelinePhases.length - 1}
              />
            ))}
          </div>
        ) : (
          // Edit mode: wrap in DndContext for drag-and-drop
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={timelinePhases.map((p) => p.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div className="relative flex items-start gap-4 w-fit">
                {timelinePhases.map((phase, index) => (
                  <SortableTimelinePhaseItem
                    key={phase.id}
                    id={phase.id}
                    phase={phase}
                    readOnly={readOnly}
                    onUpdatePhase={handleUpdatePhase}
                    onRemovePhase={handleRemovePhase}
                    isLast={index === timelinePhases.length - 1}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Add Phase Button */}
      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-muted-foreground hover:text-foreground hover:bg-muted border border-dashed border-transparent hover:border-border"
          onClick={handleAddPhase}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          {t("canvas.blocks.timeline.addPhase", "Add phase")}
        </Button>
      )}
    </div>
  );
}
